// The cockpit's half of sheets.ts: what "layers" mean on the main map, how an
// export steps through time, and what goes in the legend.
//
// LAYERS are the Layers panel's own state (categories, types, admin lines, the
// watched-only and country filters, the damage window — panels.layerState)
// plus the basemap. FIRMS and aircraft stay out on purpose: both are a fresh
// opt-in every session because they poll a shared upstream, and a sheet
// quietly switching them on would undo that.
//
// TIME is the entity tile clock (`?asof`, the replay's mechanism). A frame
// `asof:<iso>` redraws territorial control, damage and places as they stood
// then. Live events are a client-side source that knows nothing of `?asof`, so
// they are HIDDEN for a dated frame and the footer says so — a map captioned
// "as of June" carrying this morning's strikes would be a false document.
// While Replay is open it owns that clock, and a dated export is refused.
//
// The LEGEND is the territorial control inside the frame, in the colours the
// map draws it: a control map without its key is a picture, not a map.

import * as maplibregl from 'maplibre-gl';

import { RASTER_BASEMAPS } from './basemaps';
import { controlLegend } from './controllegend';
import { LIVE_LAYER_IDS, setEntitiesAsof } from './objects';
import type { LayerStateDto, PanelsApi } from './panels';
import type { SheetDto, SheetHost } from './sheets';
import { isTimeTravelling } from './timeline';

export { controlLegend, geometryBbox } from './controllegend';

export interface CockpitSheetDeps {
  panels: PanelsApi;
  basemapSelect: HTMLSelectElement;
  /** Open a Studio sheet: the Studio over its imagery area. */
  openStudioSheet: (sheet: SheetDto) => void;
  /** Arm the dock's Markup tool (which opens the rail on this map). */
  startMarkup: () => void;
}

function fmtDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function cockpitSheetHost(map: maplibregl.Map, deps: CockpitSheetDeps): SheetHost {
  // The live layers' visibility before a dated frame hid them; null = not staged.
  let liveVisibility: Map<string, string> | null = null;
  const basemapCredit = (): string =>
    RASTER_BASEMAPS[deps.basemapSelect.value]?.attribution ?? '© OpenFreeMap © OpenStreetMap contributors';

  return {
    surface: 'map',
    map,
    mount: document.body,
    frames: () => [],
    timeFrames: true,
    async stage(key) {
      if (!key) {
        // "Now" inside a dated animation: back to the live clock, live events shown.
        if (liveVisibility) {
          setEntitiesAsof(map, null);
          for (const [id, v] of liveVisibility) {
            if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', v as 'visible' | 'none');
          }
          liveVisibility = null;
        }
        return;
      }
      if (isTimeTravelling()) {
        throw new Error('Close Replay first — it owns the map’s clock, and a dated export needs it.');
      }
      if (!liveVisibility) {
        liveVisibility = new Map();
        for (const id of LIVE_LAYER_IDS) {
          if (!map.getLayer(id)) continue;
          liveVisibility.set(id, String(map.getLayoutProperty(id, 'visibility') ?? 'visible'));
          map.setLayoutProperty(id, 'visibility', 'none');
        }
      }
      setEntitiesAsof(map, key.slice('asof:'.length));
    },
    async unstage() {
      await this.stage(null);
    },
    footer(key) {
      const when = key
        ? `Territorial control and mapped objects as of ${fmtDay(key.slice('asof:'.length))} (live events not shown)`
        : `As shown ${new Date().toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
      return `${when} · Basemap ${basemapCredit()} · geosentry.dev`;
    },
    legend: (bbox) => controlLegend(deps.panels.entities(), bbox),
    captureLayers: () => ({ basemap: deps.basemapSelect.value, ...deps.panels.layerState() }),
    applyLayers(layers) {
      const basemap = layers.basemap;
      if (typeof basemap === 'string' && basemap !== deps.basemapSelect.value
          && [...deps.basemapSelect.options].some((o) => o.value === basemap)) {
        deps.basemapSelect.value = basemap;
        // The select's own handler switches the basemap and remembers it.
        deps.basemapSelect.dispatchEvent(new Event('change'));
      }
      deps.panels.applyLayerState(layers as LayerStateDto);
    },
    captureImagery: () => null,
    openElsewhere: (sheet) => deps.openStudioSheet(sheet),
    startMarkup: () => deps.startMarkup(),
    defaultTitle: () => `Map sheet · ${new Date().toLocaleDateString(undefined,
      { day: 'numeric', month: 'short', year: 'numeric' })}`,
  };
}
